import type { SavedSearchFacetChip, SavedSearchSummary } from '../../types/saved-search';
import { parseUrlToIndexUiState } from '../search-router/url-handling';
import { normalizeSavedSearchUrlInput } from './normalize-saved-search-url';

const EMPTY_SUMMARY: SavedSearchSummary = {
  locationValues: [],
  regionTagsValues: [],
  facets: [],
  pageIndex: 0,
  isGlobalScope: false,
};

/** Path + query for `<Link href>` (same-origin). */
export function savedSearchRelativeHref(storedUrl: string): string {
  if (storedUrl == null || typeof storedUrl !== 'string' || !storedUrl.trim()) return '/';
  const normalized = normalizeSavedSearchUrlInput(storedUrl);
  if (!normalized) return '/';
  try {
    const base =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://www.jobcrawls.com';
    const u = new URL(normalized, base);
    return `${u.pathname}${u.search}` || '/';
  } catch {
    const raw = String(storedUrl);
    return raw.startsWith('/') ? raw.split('#')[0] : `/${raw}`.split('#')[0];
  }
}

function pathAndSearchFromAnyUrl(input: string): string {
  if (input == null || typeof input !== 'string' || !input.trim()) return '/';
  try {
    const base =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://www.jobcrawls.com';
    const u = new URL(input, base);
    u.searchParams.delete('returnTo');
    return `${u.pathname}${u.search}`;
  } catch {
    const raw = String(input);
    const noHash = raw.split('#')[0];
    return noHash.startsWith('/') ? noHash : `/${noHash}`;
  }
}

/**
 * Derives display-oriented fields from a stored saved-search URL (absolute or relative).
 */
export function parseSavedSearchUrlToSummary(input: string): SavedSearchSummary {
  if (input == null || typeof input !== 'string' || !input.trim()) return { ...EMPTY_SUMMARY };
  const pathAndSearch = pathAndSearchFromAnyUrl(input);
  const state = parseUrlToIndexUiState(pathAndSearch);

  const queryRaw = state.query?.trim();
  const query = queryRaw && queryRaw.length > 0 ? queryRaw : undefined;

  const refinementList = state.refinementList ?? {};
  const locationValues = [...(refinementList.location ?? [])].map((v) => String(v).trim()).filter(Boolean);
  // TEC-482: the new hierarchical location system's equivalent, a saved search only ever has
  // one or the other (whichever system was active when it was saved), never both.
  const regionTagsValues = [...(refinementList.regionTags ?? [])].map((v) => String(v).trim()).filter(Boolean);

  const facets: SavedSearchFacetChip[] = Object.entries(refinementList)
    .filter(([key]) => key !== 'location' && key !== 'regionTags')
    .map(([attribute, values]) => {
      const list = (Array.isArray(values) ? values : [values as string])
        .map((v) => String(v).trim())
        .filter(Boolean);
      return { attribute, values: list };
    })
    .filter((f) => f.values.length > 0)
    .sort((a, b) => a.attribute.localeCompare(b.attribute));

  const sort =
    state.sort === 'date' || state.sort === 'salary'
      ? state.sort
      : undefined;

  const rawPage = state.page;
  const pageNum = rawPage == null || Number.isNaN(Number(rawPage)) ? 0 : Number(rawPage);
  const pageIndex = pageNum >= 1 ? pageNum : 0;

  return {
    query,
    locationValues,
    regionTagsValues,
    facets,
    sort,
    pageIndex,
    isGlobalScope: state.isFinland === false,
  };
}
